<template>
    <div class="clients-tab">
        <div class="clients-toolbar">
            <kimsa-text-field class="clients-search" :value="search" :placeholder="$t('common.search')"
                prepend-inner-icon="mdi-magnify" dense @change="onSearch" />

            <kimsa-segmented scrollable dense :items="filterOptions" :value="filter" @change="onFilter" />

            <div class="clients-actions">
                <export-button :filename="$t('business.clients')" :build="buildExport" />

                <button-tooltip top :tooltip="$t('business.plan_types')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="icon-button" v-bind="attrs" v-on="on"
                            @click="$refs.planTypesDialog.show()">
                            <v-icon size="15">mdi-tag-multiple-outline</v-icon>
                        </button>
                    </template>
                </button-tooltip>

                <v-btn depressed small outlined color="secondary" @click="$refs.quickChargeDialog.show()">
                    {{ $t('business.quick_charge') }}
                </v-btn>

                <v-btn depressed small outlined color="secondary" @click="newPayment">
                    {{ $t('business.add_payment') }}
                </v-btn>

                <v-btn depressed small color="primary" class="black--text" @click="newClient">
                    {{ $t('business.add_client') }}
                </v-btn>
            </div>
        </div>

        <clients-table :items="rows" :loading="loading" :per-page="perPage" :page="page" :toggling-id="togglingId"
            :no-data-text="emptyText" @edit="editClient" @toggle:active="toggleActive" @history="showHistory"
            @charge="chargeClient">
            <template v-slot:footer>
                <div class="clients-footer">
                    <span class="clients-count">
                        {{ $tc('business.clients_count', filtered.length, { n: filtered.length }) }}
                    </span>
                    <kimsa-pagination v-if="pageCount > 1" height="auto" :page-count="pageCount"
                        :total-visible="isMobile ? 4 : 7" @page="page = $event" />
                </div>
            </template>
        </clients-table>

        <client-dialog ref="clientDialog" :company-id="companyId" :plan-types="planTypes" :activities="activities"
            @save="onSaveClient" @delete="confirmDelete" />

        <plan-types-dialog ref="planTypesDialog" :plan-types="planTypes" :clients="clients" :currency="currency"
            :activities="activities" @change="onChangePlanTypes" @change:currency="onChangeCurrency" />

        <payment-dialog ref="paymentDialog" :clients="roster" :loading="savingPayment" @save="onSavePayment"
            @delete="onDeletePayment" />

        <payments-history-dialog ref="historyDialog" :company-id="companyId" @create="paymentFromHistory"
            @edit="editPayment" />

        <quick-charge-dialog ref="quickChargeDialog" :company-id="companyId" :clients="roster" @cell="onQuickCell" />

        <kimsa-confirm ref="deleteConfirm" :title="$t('business.delete_client')" :content="deleteContent"
            :persistent="false" alert-mode show-cancel @confirm="onDeleteClient" />
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaSegmented from "@/components/common/inputs/KimsaSegmented";
import KimsaPagination from "@/components/common/KimsaPagination";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import ClientsTable from "@/components/company/business/ClientsTable";
import ClientDialog from "@/components/company/business/ClientDialog";
import PlanTypesDialog from "@/components/company/business/PlanTypesDialog";
import PaymentDialog from "@/components/company/business/PaymentDialog";
import PaymentsHistoryDialog from "@/components/company/business/PaymentsHistoryDialog";
import QuickChargeDialog from "@/components/company/business/QuickChargeDialog";
import ExportButton from "@/components/company/business/ExportButton";
import { getAvatarGradient, getInitials, safeLower } from "@/utils/Common";
import { buildClientsSheets } from "@/utils/BusinessExport";
import {
    DEFAULT_CURRENCY, deleteClient, deletePayment, formatMoney, invalidateRoster, loadRoster, mergeRoster,
    monthsSince, nextPaymentAfter, paymentDocId, paymentStatus, saveClient, savePayment, saveSettings,
    setClientActive, setClientNextPayment, setCurrency, today,
} from "@/utils/Business";

const PER_PAGE = 15

export default {
    name: "ClientsTab",
    components: {
        KimsaTextField, KimsaSegmented, KimsaPagination, KimsaConfirm, ButtonTooltip,
        ClientsTable, ClientDialog, PlanTypesDialog, PaymentDialog, PaymentsHistoryDialog, QuickChargeDialog,
        ExportButton,
    },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        period: {
            type: String,
            default: '',
        },
        payments: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: true,
            clients: [],
            students: [],
            planTypes: [],
            activities: [],
            currency: DEFAULT_CURRENCY,
            search: '',
            filter: 'all',
            page: 1,
            perPage: PER_PAGE,
            togglingId: null,
            deleting: null,
            savingPayment: false,
        }
    },
    computed: {
        filterOptions(vm = this) {
            void vm.$i18n.locale
            return [
                { value: 'all', name: vm.$t('common.all') },
                { value: 'active', name: vm.$t('business.filter_active') },
                { value: 'debt', name: vm.$t('business.filter_debt') },
                { value: 'inactive', name: vm.$t('business.filter_inactive') },
                { value: 'app', name: vm.$t('business.filter_from_app') },
                { value: 'manual', name: vm.$t('business.filter_manual') },
            ]
        },
        roster(vm = this) {
            return mergeRoster(vm.clients, vm.students)
        },
        filtered(vm = this) {
            const search = safeLower(vm.search).trim()
            return vm.roster.filter(client => {
                const status = paymentStatus(client)
                if (vm.filter === 'active' && !client.active) return false
                if (vm.filter === 'inactive' && client.active) return false
                if (vm.filter === 'debt' && !['due', 'overdue'].includes(status)) return false
                if (vm.filter === 'app' && !client.fromApp) return false
                if (vm.filter === 'manual' && client.fromApp) return false
                if (!search) return true
                return safeLower(client.name).includes(search) || safeLower(client.email).includes(search)
            })
        },
        rows(vm = this) {
            void vm.$i18n.locale
            return vm.filtered.map(client => {
                const status = paymentStatus(client)
                return {
                    ...client,
                    key: client.id || client.studentId,
                    initials: getInitials(client.name),
                    avatarGradient: getAvatarGradient(client.studentId || client.id || client.name),
                    months: client.joinedAt ? monthsSince(client.joinedAt) : null,
                    nextPaymentLabel: client.nextPayment
                        ? vm.$moment(client.nextPayment, 'YYYY-MM-DD').format('DD MMM')
                        : vm.$t('business.no_value'),
                    planLabel: client.planAmount ? formatMoney(client.planAmount) : vm.$t('business.no_value'),
                    methodLabel: client.method
                        ? vm.$t(`business.method_${client.method}`)
                        : vm.$t('business.no_value'),
                    status,
                    statusLabel: vm.$t(`business.status_${status}`),
                    source: client.fromApp ? 'app' : 'manual',
                    sourceLabel: vm.$t(client.fromApp ? 'business.source_app' : 'business.source_manual'),
                }
            })
        },
        pageCount(vm = this) {
            return Math.max(1, Math.ceil(vm.filtered.length / vm.perPage))
        },
        emptyText(vm = this) {
            return vm.roster.length === 0 ? vm.$t('business.no_clients') : vm.$t('business.no_clients_filter')
        },
        deleteContent(vm = this) {
            const key = vm.deleting?.studentId ? 'business.delete_linked_client_body' : 'business.delete_client_body'
            return vm.$t(key, { name: vm.deleting?.name || '' })
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            const vm = this
            if (!vm.companyId) return
            vm.loading = true
            try {
                const { clients, students, planTypes, activities, currency } = await loadRoster(vm.companyId)
                vm.clients = clients
                vm.planTypes = planTypes
                vm.activities = activities
                vm.students = students
                vm.currency = currency
            } catch (error) {
                console.error('[business] loadClients', error?.code, error?.message)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        buildExport() {
            const vm = this
            return buildClientsSheets({
                roster: vm.roster,
                planTypes: vm.planTypes,
                payments: vm.payments,
                period: vm.period,
            })
        },
        onSearch(value) {
            this.search = value
            this.page = 1
        },
        onFilter(value) {
            this.filter = value
            this.page = 1
        },
        newClient() {
            this.$refs.clientDialog.show()
        },
        editClient(client) {
            const vm = this
            vm.$refs.clientDialog.show(vm.roster.find(item => (item.id || item.studentId) === client.key) || client)
        },
        storeClient(saved) {
            const vm = this
            invalidateRoster(vm.companyId)
            const index = vm.clients.findIndex(item => item.id === saved.id)
            if (index >= 0) vm.clients.splice(index, 1, saved)
            else vm.clients = [...vm.clients, saved]
        },
        async onSaveClient(client) {
            const vm = this
            try {
                vm.storeClient(await saveClient(vm.companyId, client))
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        async toggleActive(row) {
            const vm = this
            const client = vm.roster.find(item => (item.id || item.studentId) === row.key)
            if (!client) return
            const active = !client.active
            vm.togglingId = row.key

            try {
                if (client.id) {
                    const index = vm.clients.findIndex(item => item.id === client.id)
                    vm.clients.splice(index, 1, { ...vm.clients[index], active })
                    invalidateRoster(vm.companyId)
                    await setClientActive(client.id, active)
                } else {
                    vm.storeClient(await saveClient(vm.companyId, {
                        ...client,
                        joinedAt: client.joinedAt || today(),
                        active,
                    }))
                }
            } catch (error) {
                if (client.id) {
                    const index = vm.clients.findIndex(item => item.id === client.id)
                    if (index >= 0) vm.clients.splice(index, 1, { ...vm.clients[index], active: !active })
                }
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.togglingId = null
            }
        },
        newPayment() {
            this.$refs.paymentDialog.show()
        },
        chargeClient(row) {
            const vm = this
            vm.$refs.paymentDialog.show({
                client: vm.roster.find(item => (item.id || item.studentId) === row.key) || row,
            })
        },
        showHistory(row) {
            const vm = this
            vm.$refs.historyDialog.show(vm.roster.find(item => (item.id || item.studentId) === row.key) || row)
        },
        paymentFromHistory() {
            const vm = this
            vm.$refs.paymentDialog.show({ client: vm.$refs.historyDialog.client })
        },
        editPayment(payment) {
            const vm = this
            vm.$refs.paymentDialog.show({ client: vm.$refs.historyDialog.client, payment })
        },
        onQuickCell({ client, period }) {
            const vm = this
            const payment = client.id
                ? vm.$refs.quickChargeDialog.payments[paymentDocId(vm.companyId, period, client.id)] || null
                : null
            vm.$refs.paymentDialog.show({ client, period, payment })
        },
        afterPaymentChange() {
            const vm = this
            vm.$refs.paymentDialog.hide()
            vm.$refs.historyDialog.load()
            if (vm.$refs.quickChargeDialog.dialog) vm.$refs.quickChargeDialog.load()
            vm.$emit('change:payments')
        },
        async ensureClient(client) {
            const vm = this
            if (client?.id) return client
            const saved = await saveClient(vm.companyId, { ...client, joinedAt: client.joinedAt || today() })
            vm.storeClient(saved)
            return saved
        },
        async onSavePayment({ client, period, amount, method, date }) {
            const vm = this
            vm.savingPayment = true
            try {
                const target = await vm.ensureClient(client)
                await savePayment(vm.companyId, {
                    clientId: target.id, period, amount, method, date, planId: target.planId,
                })

                const nextPayment = nextPaymentAfter(target, period, date)
                if (nextPayment && nextPayment !== target.nextPayment) {
                    await setClientNextPayment(target.id, nextPayment)
                    vm.storeClient({ ...target, nextPayment })
                }

                vm.afterPaymentChange()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.savingPayment = false
            }
        },
        async onDeletePayment(payment) {
            const vm = this
            try {
                await deletePayment(payment.id)
                vm.afterPaymentChange()
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        confirmDelete(client) {
            const vm = this
            vm.deleting = client
            vm.$refs.deleteConfirm.show()
        },
        async onDeleteClient() {
            const vm = this
            const client = vm.deleting
            if (!client?.id) return
            const previous = vm.clients
            vm.clients = vm.clients.filter(item => item.id !== client.id)
            vm.deleting = null
            invalidateRoster(vm.companyId)
            try {
                await deleteClient(client.id)
                vm.$refs.clientDialog?.hide()
            } catch (error) {
                vm.clients = previous
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        async onChangePlanTypes(planTypes) {
            const vm = this
            const previous = vm.planTypes
            vm.planTypes = planTypes
            invalidateRoster(vm.companyId)
            try {
                await saveSettings(vm.companyId, { planTypes })
            } catch (error) {
                vm.planTypes = previous
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        async onChangeCurrency(currency) {
            const vm = this
            const previous = vm.currency
            if (!currency || currency === previous) return
            vm.currency = currency
            setCurrency(currency)
            invalidateRoster(vm.companyId)
            try {
                await saveSettings(vm.companyId, { currency })
            } catch (error) {
                vm.currency = previous
                setCurrency(previous)
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
    },
    watch: {
        pageCount(value) {
            if (this.page > value) this.page = value
        },
        companyId() {
            const vm = this
            vm.clients = []
            vm.students = []
            vm.planTypes = []
            vm.currency = DEFAULT_CURRENCY
            vm.load()
        },
    },
}
</script>

<style scoped>
.clients-tab {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.clients-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.clients-search {
    width: 240px;
    max-width: 100%;
}

.clients-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.icon-button {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    transition: color 0.15s, border-color 0.15s;
}

.icon-button:hover {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-outline2-base);
}

.clients-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
}

.clients-count {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 600px) {
    .clients-search {
        width: 100%;
    }

    .clients-actions {
        margin-left: 0;
        width: 100%;
        flex-wrap: wrap;
    }
}
</style>
